/*!
 * Flooritize 2.0 calculator engine
 * Six vendor-agnostic rules: area, strip, grid, linear, count, custom.
 * Every decision comes from the product's rule record (metafields); no vendor names appear here.
 * Pure functions, no DOM, no network. Works in the browser (window.FlooritizeCalc) and in Node.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.FlooritizeCalc = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';
  var TOL = 1e-6;                                   // stops floating noise adding a unit

  function n(v) { var x = typeof v === 'number' ? v : parseFloat(v); return isFinite(x) ? x : NaN; }
  function clean(x) { return parseFloat(x.toFixed(6)); }          // trims binary noise for display values
  /** Customer-facing measurement text: at most 2 decimals, trailing zeros dropped (20.083333333333332 -> "20.08", 20.5 -> "20.5", 20 -> "20"). Display only: never fed back into a calculation. */
  function dim(x) { x = n(x); return isFinite(x) ? String(Math.round((x + (x < 0 ? -1 : 1) * 1e-9) * 100) / 100) : ''; }
  function ceilUnits(x) { return x > TOL ? Math.ceil(x - TOL) : 0; }
  function cents(price) { return Math.round(n(price) * 100 + 1e-7); }
  function money(c) { return (c / 100).toFixed(2); }
  /** A whole-number quantity, or NaN. '2' and 2 pass. '1.5', 1.5, '2.0', '1.', '1,500', '2 boxes' and '' do not: they are rejected, never rounded, cut down or reinterpreted. */
  function wholeQty(v) {
    if (typeof v === 'number') return (isFinite(v) && Math.floor(v) === v) ? v : NaN;
    var s = String(v == null ? '' : v).trim();
    return /^\d+$/.test(s) ? parseInt(s, 10) : NaN;
  }
  /** The product's minimum order quantity; 1 when none is set. A minimum marked unverified (min_verified === false) is not enforced until it is confirmed with the supplier. */
  function minQty(rule) { if (rule && rule.min_verified === false) return 1; return parseInt(rule && rule.min_qty, 10) || 1; }
  /** The product's maximum order quantity, enforced only when it is marked verified (max_verified === true); otherwise 0, meaning no enforced maximum. */
  function maxQty(rule) { return (rule && rule.max_verified === true) ? (parseInt(rule.max_qty, 10) || 0) : 0; }

  /** Fields each rule needs. A missing or invalid one means the calculator is not shown for that variant. */
  var REQUIRED = {
    area: ['coverage_sf'], strip: ['unit_width_in'], grid: ['unit_width_in', 'unit_length_in'],
    linear: ['unit_length_in'], count: [], custom: []
  };

  /** Returns a list of missing fields (empty list = rule can run). */
  function validateRule(rule) {
    var missing = [];
    if (!rule || !REQUIRED.hasOwnProperty(rule.calc_rule)) return ['calc_rule'];
    if (!(n(rule.unit_price) > 0)) missing.push('unit_price');
    if (!rule.selling_unit) missing.push('selling_unit');
    REQUIRED[rule.calc_rule].forEach(function (f) { if (!(n(rule[f]) > 0)) missing.push(f); });
    return missing;
  }

  /** Which input modes the customer is offered for a rule. */
  function inputModes(rule) {
    return ({ area: ['sqft', 'rooms', 'walls', 'quantity'], strip: ['rooms', 'linear_feet', 'quantity'],
      grid: ['rooms', 'quantity'], linear: ['linear_feet', 'quantity'], count: ['quantity'], custom: ['quantity'] })[rule.calc_rule] || ['quantity'];
  }

  function calculateArea(input) {
    if (input.sqft != null) return n(input.sqft);
    var a = 0;
    (input.rooms || []).concat(input.walls || []).forEach(function (d) { a += n(d[0]) * n(d[1]); });
    (input.openings || []).forEach(function (d) { a -= n(d[0]) * n(d[1]); });
    return a;
  }
  function calculateWaste(amount, pct) { return amount * (n(pct) || 0) / 100; }

  function applyConstraints(qty, rule, notes) {
    var upc = n(rule.units_per_carton);
    if (rule.full_cartons_only && upc > 0) {
      var q2 = ceilUnits(qty / upc) * upc;
      if (q2 !== qty) { notes.push('Sold in full cartons of ' + dim(upc) + '.'); qty = q2; }
    }
    var mn = minQty(rule);                             // a quantity below the minimum is rejected by calculate(), never raised here
    var inc = parseInt(rule.qty_increment, 10) || 1;
    if (inc > 1 && qty >= mn && (qty - mn) % inc) qty = mn + ceilUnits((qty - mn) / inc) * inc;
    var mx = parseInt(rule.max_qty, 10);
    if (mx && qty > mx && !maxQty(rule)) notes.push('Maximum per order is ' + mx + ' ' + rule.selling_unit + '; contact us for more.');
    return qty;
  }

  /**
   * calculate(rule, input) -> result, or { error, missing } when the product cannot be calculated.
   * input: { sqft } | { rooms:[[w,l],..] } | { walls:[[w,h],..], openings:[[w,h],..] } | { linear_feet } | { quantity }
   * plus optional waste_pct. Dimensions are in feet.
   */
  function calculate(rule, input) {
    var missing = validateRule(rule);
    if (missing.length) return { error: 'missing_data', missing: missing };
    input = input || {};
    var kind = rule.calc_rule, notes = [], out = { calc_rule: kind, selling_unit: rule.selling_unit }, qty, entered = false;
    var wastePct = input.waste_pct != null ? n(input.waste_pct) : (n(rule.default_waste_pct) || 0);

    if (input.quantity != null) {
      qty = wholeQty(input.quantity);
      if (isNaN(qty)) return { error: 'invalid_quantity' };
      if (!(qty > 0)) return { error: 'invalid_input' };
      entered = true;
    } else if (kind === 'area') {
      var area = calculateArea(input);
      if (!(area > 0)) return { error: 'invalid_input' };
      var adj = area + calculateWaste(area, wastePct);
      qty = ceilUnits(adj / n(rule.coverage_sf));
      out.measured = String(clean(area)); out.adjusted = String(clean(adj));
    } else if (kind === 'strip') {
      var R = n(rule.unit_width_in) / 12, run;
      if (input.linear_feet != null) {
        run = n(input.linear_feet);
        if (!(run > 0)) return { error: 'invalid_input' };
        out.measured = String(clean(run * R)); out.linear_feet = String(clean(run));
      } else {
        var room = (input.rooms || [])[0]; if (!room) return { error: 'invalid_input' };
        var W = n(room[0]), L = n(room[1]); if (!(W > 0 && L > 0)) return { error: 'invalid_input' };
        var a = ceilUnits(W / R) * L, b = ceilUnits(L / R) * W, alongL = a <= b;
        run = Math.min(a, b);
        notes.push('Strips run along the ' + dim(alongL ? L : W) + ' ft side (' + ceilUnits((alongL ? W : L) / R) + ' strips).');
        out.measured = String(clean(W * L)); out.linear_feet = String(clean(run));
      }
      run += calculateWaste(run, wastePct);
      qty = ceilUnits(run / (n(rule.roll_length_ft) > 0 ? n(rule.roll_length_ft) : 1));   // linear feet, or whole rolls
    } else if (kind === 'grid') {
      var rm = (input.rooms || [])[0]; if (!rm) return { error: 'invalid_input' };
      var gw = n(rm[0]), gl = n(rm[1]); if (!(gw > 0 && gl > 0)) return { error: 'invalid_input' };
      var ta = ceilUnits(gw * 12 / n(rule.unit_width_in)), tl = ceilUnits(gl * 12 / n(rule.unit_length_in));
      var mix = (ta >= 2 && tl >= 2) ? { corner: 4, border: 2 * (ta - 2) + 2 * (tl - 2), center: (ta - 2) * (tl - 2) } : { center: ta * tl };
      var role = rule.grid_role || 'center';
      qty = mix.hasOwnProperty(role) ? mix[role] : ta * tl;
      out.measured = String(clean(gw * gl)); out.grid = ta + ' x ' + tl; out.tiles_total = ta * tl; out.tile_mix = mix;
    } else if (kind === 'linear') {
      var len = n(input.linear_feet); if (!(len > 0)) return { error: 'invalid_input' };
      var adjL = len + calculateWaste(len, wastePct);
      qty = ceilUnits(adjL * 12 / n(rule.unit_length_in));
      out.measured = String(clean(len)); out.adjusted = String(clean(adjL));
    } else {
      return { error: 'quantity_only' };                 // count / custom: ordered by plain quantity
    }

    var mn = minQty(rule), mxv = maxQty(rule), allowed = applyConstraints(qty, rule, notes);
    if (entered) {                                       // a typed quantity is used exactly as typed, or refused
      if (qty < mn) return { error: 'below_minimum', min: mn, entered: qty };
      if (allowed !== qty) return { error: 'not_sellable_quantity', entered: qty, next_allowed: allowed };
      if (mxv && qty > mxv) return { error: 'above_maximum', max: mxv, entered: qty };
    } else {                                             // a quantity worked out from measurements
      qty = allowed;
      if (qty < mn) return { error: 'below_minimum', min: mn, needed: qty };
      if (mxv && qty > mxv) return { error: 'above_maximum', max: mxv, needed: qty };
    }
    var unitCents = cents(rule.unit_price);
    out.quantity = qty; out.unit_price = money(unitCents); out.total = money(unitCents * qty); out.notes = notes;
    if (n(rule.coverage_sf) > 0) out.actual_coverage_sf = String(clean(n(rule.coverage_sf) * qty));
    return out;
  }

  return { calculate: calculate, validateRule: validateRule, inputModes: inputModes,
    calculateArea: calculateArea, calculateWaste: calculateWaste, applyConstraints: applyConstraints, ceilUnits: ceilUnits, dim: dim, wholeQty: wholeQty, minQty: minQty, maxQty: maxQty };
});

/* FL14-DEC-001 (2026-10-15, owner-authorized calculator fix): decimal measurements typed with a comma.
 * Before this fix the calculator removed the comma, so "1,5" sq. ft. or feet became 15 and "12,5" linear feet became 125.
 * This runs only in the browser, before the calculator reads a measurement field (Square feet, Length, room/wall feet and inches).
 * It hands the calculator one plain number: 1.5 and 1,5 -> 1.5; 1,500 and 1,500.5 -> thousands separators removed;
 * "1," or "1." while typing -> 1; anything else with a comma, or more than one point (e.g. 1.5.2) -> empty (not read, never guessed).
 * The customer keeps seeing exactly what they typed. Quantity fields (whole numbers only) and the calculation engine are unchanged. */
(function () {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  function decimalText(t) {
    var s = String(t == null ? '' : t);
    if (/^\d{1,3}(,\d{3})+(\.\d*)?$/.test(s)) s = s.replace(/,/g, '');
    else if (/^\d*,\d{0,2}$/.test(s)) s = s.replace(',', '.');
    if (s.indexOf(',') > -1 || (s.match(/\./g) || []).length > 1) return '';
    return s;
  }
  function measurementField(el) {
    var k = el && el.getAttribute && el.getAttribute('data-k');
    return !!(k && k !== 'qty' && el.closest && el.closest('[data-flz-inputs]'));
  }
  document.addEventListener('input', function (e) {
    var el = e.target; if (!measurementField(el)) return;
    var typed = String(el.value).replace(/[^0-9.,]/g, '');
    el.__flzTyped = typed;
    el.value = decimalText(typed);
  }, true);
  document.addEventListener('input', function (e) {
    var el = e.target; if (!measurementField(el) || el.__flzTyped == null) return;
    if (el.value !== el.__flzTyped) el.value = el.__flzTyped;
    el.__flzTyped = null;
  }, false);
  window.FlooritizeDecimalText = decimalText;
})();
